import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// "I think this is wrong" → confirm → Opus 4.7 re-solves fresh → result
// Tucked inside the Final Answer card. On a disagreeing result, fires the
// parent's onAnswerUpdated so the card can flip to the new value.

interface Props {
  problem: { image?: string; text?: string; note?: string };
  currentAnswer: string;
  chapter?: string | null;
  topic?: string;
  deviceId: string;
  onAnswerUpdated?: (newAnswer: string) => void;
}

interface RecheckResult {
  new_answer: string;
  agrees: boolean;
  reasoning: string;
  original_answer: string;
  verdict: 'confirmed' | 'corrected';
  model: string;
}

type Stage = 'resting' | 'confirming' | 'loading' | 'result';

const LOADING_STEPS = [
  'Re-reading your photo with fresh eyes',
  'Identifying which textbook problem',
  'Re-solving from scratch with Opus 4.7',
  'Comparing with the original answer',
];

export default function RecheckButton({
  problem,
  currentAnswer,
  chapter,
  topic,
  deviceId,
  onAnswerUpdated,
}: Props) {
  const [stage, setStage] = useState<Stage>('resting');
  const [result, setResult] = useState<RecheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingStep, setLoadingStep] = useState(0);

  const runRecheck = async () => {
    setStage('loading');
    setError(null);
    setLoadingStep(0);

    // Step animation — advances every 4s to fill the ~15s Opus call.
    const stepTimer = setInterval(() => {
      setLoadingStep((s) => Math.min(s + 1, LOADING_STEPS.length - 1));
    }, 4000);

    try {
      const res = await fetch('/api/recheck', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-device-id': deviceId,
        },
        body: JSON.stringify({
          problem,
          original_answer: currentAnswer,
          chapter,
          topic,
        }),
      });
      clearInterval(stepTimer);
      const data = (await res.json()) as (RecheckResult & { error?: string }) | { error: string };
      if (!res.ok || 'error' in data) {
        setError(
          (data as { error?: string }).error === 'hint_limit_reached'
            ? "We've hit today's limit for re-checks. Try again tomorrow!"
            : "The re-check couldn't finish. Try again in a moment."
        );
        setStage('confirming');
        return;
      }
      const r = data as RecheckResult;
      setResult(r);
      setStage('result');
      if (!r.agrees && onAnswerUpdated) onAnswerUpdated(r.new_answer);
    } catch {
      clearInterval(stepTimer);
      setError('Network hiccup. Try again in a moment.');
      setStage('confirming');
    }
  };

  return (
    <AnimatePresence mode="wait">
      {stage === 'resting' && (
        <motion.div
          key="rest"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="pt-3 mt-3 border-t border-dashed border-ink/15 flex justify-between items-center gap-3"
        >
          <span className="text-[11px] text-ink/50 shrink-0">Not sure this is right?</span>
          <button
            onClick={() => setStage('confirming')}
            className="inline-flex items-center gap-1.5 text-coral font-bold text-xs hover:text-coral/80 transition"
          >
            <span>🤔</span>
            <span>I think this is wrong</span>
            <span>→</span>
          </button>
        </motion.div>
      )}

      {stage === 'confirming' && (
        <motion.div
          key="confirm"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="mt-3 p-4 bg-sun/20 rounded-xl border-2 border-sun/40 space-y-2"
        >
          <div className="font-display font-black text-sm flex items-center gap-2">
            <span>🔍</span>
            <span>Get a fresh second opinion?</span>
          </div>
          <div className="text-xs text-ink/75 leading-relaxed">
            Our strongest AI (Claude Opus 4.7) will re-read your photo and solve the problem from scratch, ignoring the previous answer.
          </div>
          <div className="text-[11px] text-ink/55 leading-relaxed">
            Takes about 20 seconds · counts as 1 of your daily questions.
          </div>
          {error && <div className="text-xs text-coral font-semibold">{error}</div>}
          <div className="flex gap-2 pt-1">
            <button onClick={runRecheck} className="btn-primary text-sm px-4 py-2">
              Yes, re-check →
            </button>
            <button
              onClick={() => {
                setStage('resting');
                setError(null);
              }}
              className="btn-ghost text-sm px-4 py-2"
            >
              Not now
            </button>
          </div>
        </motion.div>
      )}

      {stage === 'loading' && (
        <motion.div
          key="loading"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="mt-3 p-4 bg-violet/10 rounded-xl border-2 border-violet/30 space-y-2"
        >
          <div className="font-display font-black text-sm flex items-center gap-2">
            <span>🔍</span>
            <span>Taking a fresh look…</span>
          </div>
          <div className="text-xs text-ink/70">
            Using Claude Opus 4.7 — our strongest model.
          </div>
          <div className="space-y-1.5 pt-2">
            {LOADING_STEPS.map((step, i) => (
              <div
                key={i}
                className={`flex items-center gap-2 text-xs py-1.5 px-2.5 rounded-lg ${
                  i < loadingStep
                    ? 'bg-white text-ink/70'
                    : i === loadingStep
                      ? 'bg-white text-ink font-semibold'
                      : 'bg-white/40 text-ink/40'
                }`}
              >
                {i < loadingStep ? (
                  <span className="text-sky w-3.5 text-center">✓</span>
                ) : i === loadingStep ? (
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-3.5 h-3.5 border-2 border-violet/20 border-t-violet rounded-full inline-block"
                  />
                ) : (
                  <span className="w-3.5 text-center">·</span>
                )}
                <span>{step}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {stage === 'result' && result && (
        <motion.div
          key="result"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-3 rounded-xl p-4 space-y-3 border-2 ${
            result.verdict === 'corrected'
              ? 'bg-coral/10 border-coral/30'
              : 'bg-sky/10 border-sky/30'
          }`}
        >
          <div className="flex items-start gap-2.5">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-black ${
                result.verdict === 'corrected'
                  ? 'bg-coral/20 text-coral'
                  : 'bg-sky/20 text-sky'
              }`}
            >
              {result.verdict === 'corrected' ? '!' : '✓'}
            </div>
            <div className="flex-1">
              <div className="font-display font-black text-sm leading-tight">
                {result.verdict === 'corrected'
                  ? 'Good catch — the first answer was off.'
                  : 'The original answer holds up.'}
              </div>
              {result.verdict === 'corrected' && (
                <div className="mt-2">
                  <div className="font-display font-black text-2xl leading-tight text-ink">
                    {result.new_answer}
                  </div>
                  <div className="text-xs text-ink/50 font-display font-bold line-through mt-0.5">
                    was {result.original_answer}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="text-xs text-ink/75 leading-relaxed">{result.reasoning}</div>
          <div className="text-[10px] text-ink/45 italic">
            Verified by Claude Opus 4.7 · our strongest model
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
